
export enum Biomes {
  AMAZONIA,
  CAATINGA,
  CERRADO,
  PANTANAL,
  MATA_ATRANTICA,
  PAMPA
}

export const BiomesRercord: Record<string, string> = {
  'AMAZONIA': 'AMAZONIA',
  'CAATINGA': 'CAATINGA',
  'CERRADO': 'CERRADO',
  'PANTANAL': 'PANTANAL',
  'MATA_ATRANTICA': 'MATA ATLÂNTICA',
  'PAMPA': 'PAMPA'
};

export const BiomesOnPageRercord: Record<string, string> = {
  'AMAZONIA': 'AMAZONIA',
  'CAATINGA': 'CAATINGA',
  'CERRADO': 'CERRADO',
  'PANTANAL': 'PANTANAL',
  'MATA ATLÂNTICA': 'MATA_ATRANTICA',
  'PAMPA': 'PAMPA'
};

export enum ProjectStatus {
  PROJECT_PENDING_AUDITOR_VALIDATION = 'PENDENTE DE VALIDAÇÃO DO AUDITOR',
  PROJECT_APPROVED_BY_AUDITOR = 'APROVADO PELO AUDITOR',
  PROJECT_REJECTED_BY_AUDITOR = 'REJEITADO PELO AUDITOR',
  PROJECT_ADJUSTED_BY_DEVELOPER = 'AJUSTADO PELO DESENVOLVEDOR',
  PROJECT_PENDING_DEVELOPER_CONFIRMATION = 'PENDENTE DE CONFIRMACAO DO DESENVOLVEDOR',
  PROJECT_CONFIRMED_BY_DEVELOPER = 'CONFIRMADO PELO DESENVOLVEDOR',
  PROJECT_REJECTED_BY_DEVELOPER = 'REJEITADO PELO DESENVOLVEDOR',
  PROJECT_REGISTERED_IN_B3 = 'REGISTRADO NA B3',
  PROJECT_DRAFT = 'RASCUNHO'
}

export const PROJECT_STATUS_DISPLAY_LABELS: Record<string, string> = {
  [ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION]: 'Pendente de verificação do auditor',
  [ProjectStatus.PROJECT_APPROVED_BY_AUDITOR]: 'Aprovado pelo auditor',
  [ProjectStatus.PROJECT_REJECTED_BY_AUDITOR]: 'Rejeitado pelo auditor',
  [ProjectStatus.PROJECT_ADJUSTED_BY_DEVELOPER]: 'Ajustado pelo desenvolvedor',
  [ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION]: 'Pendente de confirmação do desenvolvedor',
  [ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER]: 'Confirmado pelo desenvolvedor',
  [ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER]: 'Rejeitado pelo desenvolvedor',
  [ProjectStatus.PROJECT_REGISTERED_IN_B3]: 'Registrado na B3',
  [ProjectStatus.PROJECT_DRAFT]: 'Rascunho',
};

export function getProjectStatusDisplayLabel(status?: string): string {
  if (!status) {
    return '-';
  }

  const normalizedStatus = normalizeProjectStatus(status);
  return PROJECT_STATUS_DISPLAY_LABELS[normalizedStatus] ?? status;
}

export function getProjectStatusIcon(status?: string): string {
  const normalizedStatus = normalizeProjectStatus(status);

  switch (normalizedStatus) {
    case ProjectStatus.PROJECT_REGISTERED_IN_B3:
    case ProjectStatus.PROJECT_APPROVED_BY_AUDITOR:
    case ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER:
      return 'check_circle';
    case ProjectStatus.PROJECT_REJECTED_BY_AUDITOR:
    case ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER:
      return 'do_not_disturb_on';
    case ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION:
    case ProjectStatus.PROJECT_ADJUSTED_BY_DEVELOPER:
    case ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION:
    case ProjectStatus.PROJECT_DRAFT:
      return 'schedule';
    default:
      return 'schedule';
  }
}

const PROJECT_STATUS_CODE_TO_ENUM: Record<string, ProjectStatus> = {
  PROJECT_PENDING_AUDITOR_VALIDATION: ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION,
  PROJECT_APPROVED_BY_AUDITOR: ProjectStatus.PROJECT_APPROVED_BY_AUDITOR,
  PROJECT_REJECTED_BY_AUDITOR: ProjectStatus.PROJECT_REJECTED_BY_AUDITOR,
  PROJECT_ADJUSTED_BY_DEVELOPER: ProjectStatus.PROJECT_ADJUSTED_BY_DEVELOPER,
  PROJECT_PENDING_DEVELOPER_CONFIRMATION: ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION,
  PROJECT_CONFIRMED_BY_DEVELOPER: ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER,
  PROJECT_REJECTED_BY_DEVELOPER: ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER,
  PROJECT_REGISTERED_IN_B3: ProjectStatus.PROJECT_REGISTERED_IN_B3,
  PROJECT_DRAFT: ProjectStatus.PROJECT_DRAFT,
};

export function normalizeProjectStatus(status?: string): string {
  if (!status?.trim()) {
    return status ?? '';
  }

  return PROJECT_STATUS_CODE_TO_ENUM[status] ?? status;
}

export enum ProjectStatusCode {
  PROJECT_PENDING_AUDITOR_VALIDATION = 'PROJECT_PENDING_AUDITOR_VALIDATION',
  PROJECT_APPROVED_BY_AUDITOR = 'PROJECT_APPROVED_BY_AUDITOR',
  PROJECT_REJECTED_BY_AUDITOR = 'PROJECT_REJECTED_BY_AUDITOR',
  PROJECT_ADJUSTED_BY_DEVELOPER = 'PROJECT_ADJUSTED_BY_DEVELOPER',
  PROJECT_PENDING_DEVELOPER_CONFIRMATION = 'PROJECT_PENDING_DEVELOPER_CONFIRMATION',
  PROJECT_CONFIRMED_BY_DEVELOPER = 'PROJECT_CONFIRMED_BY_DEVELOPER',
  PROJECT_REJECTED_BY_DEVELOPER = 'PROJECT_REJECTED_BY_DEVELOPER',
  PROJECT_REGISTERED_IN_B3 = 'PROJECT_REGISTERED_IN_B3'
}

export enum ProjectStatusFilter {
  ALL = 'Todos',
  PROJECT_PENDING_AUDITOR_VALIDATION = 'Pendente de verificação do auditor',
  PROJECT_REJECTED_BY_AUDITOR = 'Rejeitado pelo auditor',
  PROJECT_PENDING_DEVELOPER_CONFIRMATION = 'Pendente de confirmação do desenvolvedor',
  PROJECT_REJECTED_BY_DEVELOPER = 'Rejeitado pelo desenvolvedor',
  PROJECT_REGISTERED_IN_B3 = 'Registrado na B3',
  PROJECT_DRAFT = 'Rascunho',
}

export type BadgeProjectStatus = 
| 'PENDENTE DE VALIDAÇÃO DO AUDITOR'
| 'APROVADO PELO AUDITOR'
| 'REJEITADO PELO AUDITOR'
| 'AJUSTADO PELO DESENVOLVEDOR'
| 'PENDENTE DE CONFIRMACAO DO DESENVOLVEDOR'
| 'CONFIRMADO PELO DESENVOLVEDOR'
| 'REJEITADO PELO DESENVOLVEDOR'
| 'REGISTRADO NA B3'
| 'RASCUNHO';

interface SelectItem {
  inputId: any | null;
  label: string | null;
  data: number | null;
}

export interface ProjectMeta {
  totalPages?: number;
  totalRecords?: number;
}

export interface ProjectUpdateStatus {
  status:string,
  description?:string
}

export interface Developer {
  developerName?: string;
  cpfCnpj?: string;
  email?: string;
  phone?: string;
  principal?: boolean;
  id?: number;
}

export interface Locations {
  landOwnerFullName?: string;
  landOwnerCpfCnpj?: string;
  latitude?: string;
  longitude?: string;
  country?: string;
  state?: string;
  municipality?: string;
  totalHectares?: number;
  carbonGenerationHectares?: number;
  availableHectares?: number;
  biome?: string;
  registrationRecord?: string;
  registrationRecordLink?: string;
  id?: number;

  // usado somente para mostrar em tela
  coordinates?: string;
}

export interface Attachment {
  link?: string;
  type?: string;
  id?: number;
}

export interface Ods {
  description?: string;
}

export interface PsaItemsProjectData {
  carbonProjects?: PsaProjectData[];
}

export interface PsaProjectData {
  projectId?: number | null;
  status?: string;
  insertDate?: string,

  projectName?: string | null;
  registerCode?: string | null;
  projectStartDate?: string | null;
  projectEndDate?: string | null;
  baselineActivityId?: number | null;
  generalDetails?: string | null;
  developers?: Developer[] | null;

  locations?: Locations[] | null;

  methodologyId?: number | null;
  methodologyReportLink?: string | null;
  methodologyDocumentDescription?: string | null;
  otherDocumentationDescription?: string | null;
  attachments?: Attachment[] | null;

  odsNumbers?: number[] | null;
  coBenefits?: string | null;

  // se foi rejeitado pelo auditor ou desenvolvedor
  reasonRejection?: string
  showReason?: boolean

  /** Indica se o registro veio do endpoint de consulta unificada como rascunho. */
  isDraft?: boolean;
}

export interface PsaProjectRegisterFormRegister {
  projectName?: string | null;
  registerCode?: string | null;
  projectStartDate?: string | null;
  baselineActivity?: SelectItem | null;
  generalDetails?: string | null;
  developerCompanyName?: string | null;
  developerCnpj?: string | null;
  developerEmail?: string | null;
  developerPhone?: string | null;
  developers?: Developer[] | null;

  landOwnerFullName?: string | null;
  landOwnerCpf?: string | null;
  lat?: string | null;
  long?: string | null;
  country?: string | null;
  uf?: SelectItem | null;
  city?: SelectItem | null;
  totalHectares?: number | null;
  carbonGenerationHectares?: number | null;
  availableHectares?: number | null;
  biome?: SelectItem | null;
  registrationRecord?: string | null;
  onlineStatementLink?: string | null;
  locations?: Locations[] | null;
  
  methodology?: SelectItem | null | null;
  ramComplianceReportLink?: string | null;
  ramComplianceReportDetail?: string | null;
  documentDescription?: string | null;
  attachments?: Attachment[] | null;
  

  odsNumbers?: number[] | null;
  coBenefits?: string | null;
}

export interface PsaProjectRegisterRequest {
  projectName?: string | null;
  registerCode?: string | null;
  projectStartDate?: string | null;
  baselineActivityId?: number | null;
  generalDetails?: string | null;
  developers?: Developer[] | null;

  locations?: Locations[] | null;

  methodologyId?: number | null;
  methodologyReportLink?: string | null;
  methodologyDocumentDescription?: string | null;
  otherDocumentationDescription?: string | null;
  attachments?: Attachment[] | null;

  odsNumbers?: number[] | null;
  coBenefits?: string | null;

  // se foi rejeitado pelo auditor ou desenvolvedor
  reasonRejection?: string
  showReason?: boolean
}

export interface PsaProjectResponse {
  data?: PsaProjectData;
  meta?: ProjectMeta
}

export interface PsaProjectsResponse {
  data?: PsaItemsProjectData;
  meta?: ProjectMeta
}

export interface PsaProjectFilter {
  status?: string | null;
  projectName?: string | null;
  registrationRecord?: string | null;
  insertDate?: string | null;
  page?: number | null;
  pageSize?: number | null;
}